import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../context/AuthContext';
import { useAuthErrorHandler } from '../../utils/authUtils';
import type { ReportRecipient, ReportRecipientInput } from '../../services/adminService';
import {
  createReportRecipient,
  deleteReportRecipient,
  getReportRecipients,
  updateReportRecipient,
} from '../../services/adminService';

interface RecipientFormState {
  email: string;
  name: string;
  is_active: boolean;
  receives_weekly: boolean;
  receives_monthly: boolean;
}

const emptyForm: RecipientFormState = {
  email: '',
  name: '',
  is_active: true,
  receives_weekly: true,
  receives_monthly: false,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ReportRecipientsPage: React.FC = () => {
  const { token } = useAuth();
  const { handleAuthError } = useAuthErrorHandler();

  const [recipients, setRecipients] = useState<ReportRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReportRecipient | null>(null);
  const [form, setForm] = useState<RecipientFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<ReportRecipient | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadRecipients = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await getReportRecipients(token);
      setRecipients(rows);
    } catch (err) {
      if (!handleAuthError(err)) {
        setError(err instanceof Error ? err.message : 'Failed to load recipients.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, handleAuthError]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (recipient: ReportRecipient) => {
    setEditing(recipient);
    setForm({
      email: recipient.email,
      name: recipient.name ?? '',
      is_active: Boolean(recipient.is_active),
      receives_weekly: Boolean(recipient.receives_weekly),
      receives_monthly: Boolean(recipient.receives_monthly),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const submitForm = async () => {
    if (!token) return;
    const email = form.email.trim();
    const name = form.name.trim();

    if (!EMAIL_REGEX.test(email)) {
      setFormError('Enter a valid email address.');
      return;
    }

    if (!form.receives_weekly && !form.receives_monthly) {
      setFormError('Recipient must be subscribed to at least one report.');
      return;
    }

    setSaving(true);
    setFormError(null);
    setActionError(null);
    setActionSuccess(null);

    try {
      const payload: ReportRecipientInput = {
        email,
        name: name || null,
        is_active: form.is_active,
        receives_weekly: form.receives_weekly,
        receives_monthly: form.receives_monthly,
      };

      if (editing) {
        const updated = await updateReportRecipient(editing.id, payload, token);
        setRecipients(prev => prev.map(r => (r.id === updated.id ? updated : r)));
        setActionSuccess('Recipient updated.');
      } else {
        const created = await createReportRecipient(payload, token);
        setRecipients(prev => [...prev, created].sort((a, b) => a.email.localeCompare(b.email)));
        setActionSuccess('Recipient added.');
      }
      setDialogOpen(false);
    } catch (err) {
      if (!handleAuthError(err)) {
        setFormError(err instanceof Error ? err.message : 'Failed to save recipient.');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (recipient: ReportRecipient) => {
    if (!token) return;
    const next = !recipient.is_active;
    setActionError(null);
    setActionSuccess(null);
    try {
      const updated = await updateReportRecipient(recipient.id, { is_active: next }, token);
      setRecipients(prev => prev.map(r => (r.id === updated.id ? updated : r)));
    } catch (err) {
      if (!handleAuthError(err)) {
        setActionError(err instanceof Error ? err.message : 'Failed to update recipient.');
      }
    }
  };

  const performDelete = async () => {
    if (!token || !confirmDelete) return;
    setDeleting(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await deleteReportRecipient(confirmDelete.id, token);
      setRecipients(prev => prev.filter(r => r.id !== confirmDelete.id));
      setActionSuccess('Recipient removed.');
      setConfirmDelete(null);
    } catch (err) {
      if (!handleAuthError(err)) {
        setActionError(err instanceof Error ? err.message : 'Failed to delete recipient.');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Report Recipients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            People who receive the Friday weekly report email. Inactive recipients are skipped.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Recipient
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
      {actionSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>{actionSuccess}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : recipients.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No recipients yet. Add one to start receiving the weekly report.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Recipient
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Subscriptions</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipients.map(recipient => (
                <TableRow key={recipient.id} hover>
                  <TableCell>{recipient.email}</TableCell>
                  <TableCell sx={{ color: recipient.name ? 'text.primary' : 'text.disabled' }}>
                    {recipient.name || 'No name'}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ '& .MuiTypography-root': { fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 } }}>
                      <Typography sx={{ color: recipient.receives_weekly ? 'primary.main' : 'text.disabled' }}>Weekly</Typography>
                      <Typography sx={{ color: 'text.disabled' }}>·</Typography>
                      <Typography sx={{ color: recipient.receives_monthly ? 'primary.main' : 'text.disabled' }}>Monthly</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={Boolean(recipient.is_active)}
                      onChange={() => toggleActive(recipient)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(recipient)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setConfirmDelete(recipient)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              size="small"
              value={form.email}
              onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
              autoFocus
            />
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              helperText="Optional, for reference"
            />
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.04em' }}>
                Subscriptions
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.receives_weekly}
                      onChange={e => setForm(prev => ({ ...prev, receives_weekly: e.target.checked }))}
                    />
                  }
                  label="Weekly report (Friday 8 AM)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.receives_monthly}
                      onChange={e => setForm(prev => ({ ...prev, receives_monthly: e.target.checked }))}
                    />
                  }
                  label="Monthly report (1st of month, 8 AM)"
                />
              </FormGroup>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                checked={form.is_active}
                onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                color="primary"
              />
              <Typography variant="body2">{form.is_active ? 'Active' : 'Inactive'}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submitForm} disabled={saving}>
            {saving ? 'Saving...' : editing ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => !deleting && setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Recipient</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{confirmDelete?.email}</strong> from the weekly report list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={performDelete} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
